import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Monitor, MapPin, User } from 'lucide-react';
import { http } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function SolicitudServicioModal({ onClose, onSuccess, equipos = [] }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [misEquipos, setMisEquipos] = useState(equipos);
  const [loadingEquipos, setLoadingEquipos] = useState(equipos.length === 0);
  const [sucursales, setSucursales] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(true);

  const [formData, setFormData] = useState({
    equipo_id: '',
    equipo_nombre_manual: '', // Para equipos no listados
    sucursal: '',
    descripcion_problema: '',
    usuario_afectado: '',
    prioridad: 'media'
  });

  useEffect(() => {
    if (equipos.length === 0) {
      cargarEquipos();
    }
    cargarSucursales();
  }, []);

  const cargarSucursales = async () => {
    try {
      setLoadingSucursales(true);
      // Si el usuario es cliente, usamos su ID para obtener sucursales
      if (user?.cliente_id) {
        const res = await http.get(`/clientes/${user.cliente_id}/sucursales`);
        setSucursales(res.data || []);
      }
    } catch (err) {
      console.error('Error cargando sucursales:', err);
      // No bloqueamos si falla, el usuario puede escribirla
    } finally {
      setLoadingSucursales(false);
    }
  };

  const cargarEquipos = async () => {
    try {
      setLoadingEquipos(true);
      const res = await http.get('/cliente/mis-equipos');
      setMisEquipos(res.data || []);
    } catch (err) {
      console.error('Error cargando equipos:', err);
      setError('No se pudieron cargar sus equipos. Por favor intente nuevamente.');
    } finally {
      setLoadingEquipos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.sucursal) {
        throw new Error('Debe seleccionar una sucursal');
      }
      if (!formData.descripcion_problema.trim()) {
        throw new Error('Debe describir el problema');
      }

      let equipoSeleccionado = null;
      let equipoNombre = formData.equipo_nombre_manual;
      let equipoDescripcion = '';

      if (formData.equipo_id && formData.equipo_id !== 'otro') {
        equipoSeleccionado = misEquipos.find(eq => eq.id === formData.equipo_id || eq._id === formData.equipo_id);
        if (equipoSeleccionado) {
          equipoNombre = equipoSeleccionado.nombre;
          equipoDescripcion = `${equipoSeleccionado.tipo_equipo || ''} ${equipoSeleccionado.marca || ''} ${equipoSeleccionado.modelo || ''}`.trim();
        }
      } else if (!equipoNombre) {
         // Si no seleccionó equipo y no escribió nombre manual
         throw new Error('Debe seleccionar un equipo o especificar uno nuevo');
      }

      // Construir descripción completa incluyendo usuario afectado
      let descripcionFinal = formData.descripcion_problema;
      
      const payload = {
        cliente_id: user?.cliente_id || user?.id || user?._id, // Asegurar ID de cliente
        sucursal: formData.sucursal,
        equipo_id: (formData.equipo_id === 'otro' ? null : formData.equipo_id),
        equipo_nombre: equipoNombre,
        equipo_descripcion: equipoDescripcion,
        descripcion_problema: descripcionFinal,
        usuario_afectado: formData.usuario_afectado,
        prioridad: formData.prioridad || 'media',
        estado: 'pendiente'
      };

      await http.post('/tickets', payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creando solicitud:', err);
      setError(err.response?.data?.detail || err.message || 'Error al enviar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Si cambia la sucursal, limpiar equipo seleccionado si no pertenece a esa sucursal
    if (name === 'sucursal') {
        setFormData(prev => ({ ...prev, equipo_id: '' }));
    }
  };

  // Filtrar equipos por sucursal seleccionada
  const equiposFiltrados = formData.sucursal 
    ? misEquipos.filter(eq => !eq.ubicacion || eq.ubicacion === formData.sucursal || eq.sucursal === formData.sucursal)
    : misEquipos;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Nueva Solicitud de Soporte</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="overflow-y-auto p-6">
          <form id="solicitud-form" onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {/* Sucursal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                {sucursales.length > 0 ? (
                  <select
                    name="sucursal"
                    value={formData.sucursal}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                    disabled={loadingSucursales}
                  >
                    <option value="">Seleccione una sucursal...</option>
                    {sucursales.map((suc, idx) => (
                      <option key={idx} value={suc}>{suc}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="sucursal"
                    value={formData.sucursal}
                    onChange={handleChange}
                    placeholder="Ej. Oficina Central"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                )}
              </div>
            </div>

            {/* Equipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Equipo afectado <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Monitor className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  name="equipo_id"
                  value={formData.equipo_id}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                  disabled={loadingEquipos || !formData.sucursal}
                >
                  <option value="">
                    {!formData.sucursal ? 'Seleccione sucursal primero...' : 'Seleccione un equipo...'}
                  </option>
                  {equiposFiltrados.map(equipo => (
                    <option key={equipo.id || equipo._id} value={equipo.id || equipo._id}>
                      {equipo.nombre || 'Sin nombre'} - {equipo.modelo || 'Sin modelo'} ({equipo.numero_serie || 'S/N'})
                    </option>
                  ))}
                  <option value="otro">+ Otro / Equipo no listado</option>
                </select>
              </div>
              {loadingEquipos && <p className="text-xs text-gray-500 mt-1">Cargando equipos...</p>}
            </div>

            {/* Equipo Manual (si selecciona "otro") */}
            {formData.equipo_id === 'otro' && (
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100 animate-in fade-in slide-in-from-top-2">
                <label className="block text-sm font-medium text-emerald-900 mb-1">
                  Nombre/Descripción del equipo nuevo
                </label>
                <input
                  type="text"
                  name="equipo_nombre_manual"
                  value={formData.equipo_nombre_manual}
                  onChange={handleChange}
                  placeholder="Ej. Laptop Dell nueva de RRHH"
                  className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
              </div>
            )}

            {/* Usuario Afectado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usuario afectado (Opcional)
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  name="usuario_afectado"
                  value={formData.usuario_afectado}
                  onChange={handleChange}
                  placeholder="¿Quién reporta el problema?"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción del problema <span className="text-red-500">*</span>
              </label>
              <textarea
                name="descripcion_problema"
                value={formData.descripcion_problema}
                onChange={handleChange}
                rows={4}
                placeholder="Describa detalladamente qué le sucede al equipo..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="solicitud-form"
            disabled={loading || loadingEquipos}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Save size={18} />
                Enviar Solicitud
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
